import EditIcon from "@mui/icons-material/EditOutlined";
import { IconButton, Paper, Switch, Tooltip } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import StatusChip from "@entrilia/shared/components/StatusChip";
import type { MetricExtensionRow } from "./metricExtensionsManagementState";
import RenameMetricExtensionDialog from "./RenameMetricExtensionDialog";

interface Props {
  rows: MetricExtensionRow[];
  onVisibilityChange: (rowId: string, visible: boolean) => void;
  onRename: (extensionId: string, newExtensionValue: string) => void;
}

const getColumnDefinitions = (
  onVisibilityChange: (rowId: string, visible: boolean) => void,
  onRename: (row: MetricExtensionRow) => void
): GridColDef<MetricExtensionRow>[] => [
  {
    field: "extensionValue",
    headerName: "Name",
    flex: 4,
  },
  {
    field: "source",
    headerName: "Source",
    flex: 1,
    minWidth: 150,
    renderCell: () => <StatusChip label="Company-specific" color={(t) => t.palette.info.main} />,
  },
  {
    field: "hidden",
    headerName: "Visibility",
    flex: 1,
    minWidth: 150,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => (
      <Switch checked={!row.hidden} onChange={(_, checked) => onVisibilityChange(row.id, checked)} />
    ),
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 80,
    align: "right",
    headerAlign: "right",
    renderCell: ({ row }) => (
      <Tooltip title="Rename" arrow>
        <IconButton onClick={() => onRename(row)}>
          <EditIcon />
        </IconButton>
      </Tooltip>
    ),
  },
];

const MetricExtensionManagementTable = ({ rows, onVisibilityChange, onRename }: Props) => {
  const [renamingRow, setRenamingRow] = useState<MetricExtensionRow>();

  const columns = useMemo(() => getColumnDefinitions(onVisibilityChange, setRenamingRow), [onVisibilityChange]);

  return (
    <>
      <Paper variant="outlined" sx={{ p: 1, flexGrow: 1 }}>
        <DataGrid<MetricExtensionRow>
          rows={rows}
          columns={columns}
          disableColumnReorder
          disableColumnSorting
          disableRowSelectionOnClick
          disableColumnResize
          noRowsText="No company-specific extensions found"
        />
      </Paper>

      {renamingRow && (
        <RenameMetricExtensionDialog
          extensionRow={renamingRow}
          onClose={() => setRenamingRow(undefined)}
          onRename={onRename}
        />
      )}
    </>
  );
};

export default MetricExtensionManagementTable;
